import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress
} from '@mui/material';
import {
  CleaningServices,
  Warning,
  ExpandMore,
  Visibility,
  Delete
} from '@mui/icons-material';
import axios from 'axios';

interface CleanupPreview {
  malformedEntries: Array<{
    _id: string;
    artistName: string;
    createdAt: string;
    updatedAt: string;
    issues: {
      emptyArtistName: boolean;
      noMembers: boolean;
      noContent: boolean;
    };
  }>;
  emptyInstrumentationEntries: Array<{
    _id: string;
    artistName: string;
    createdAt: string;
    updatedAt: string;
  }>;
  malformedInstrumentationEntries: Array<{
    _id: string;
    artistName: string;
    instrumentationIssues: any[];
    createdAt: string;
    updatedAt: string;
  }>;
  summary: {
    totalMalformed: number;
    totalEmptyInstrumentation: number;
    totalMalformedInstrumentation: number;
    totalToProcess: number;
  };
}

interface CleanupResult {
  success: boolean;
  message: string;
  statistics: {
    deletedMalformed: number;
    deletedEmpty: number;
    fixedInstrumentation: number;
    totalProcessed: number;
  };
  deletedEntries: Array<{
    _id: string;
    artistName: string;
    createdAt: string;
    updatedAt: string;
  }>;
}

const DatabaseCleanup: React.FC = () => {
  const [preview, setPreview] = useState<CleanupPreview | null>(null);
  const [cleanupResult, setCleanupResult] = useState<CleanupResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);

  const fetchPreview = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/bandrecommendations/cleanup/preview`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        }
      );
      
      setPreview(response.data.preview);
      setPreviewDialogOpen(true);
    } catch (err: any) {
      console.error('Error fetching cleanup preview:', err);
      setError(err.response?.data?.message || 'Failed to fetch cleanup preview');
    } finally {
      setLoading(false);
    }
  };

  const executeCleanup = async () => {
    setLoading(true);
    setError(null);
    setConfirmDialogOpen(false);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/bandrecommendations/cleanup`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        }
      );
      
      setCleanupResult(response.data);
      setPreview(null); // Clear preview after cleanup
    } catch (err: any) {
      console.error('Error executing cleanup:', err);
      setError(err.response?.data?.message || 'Failed to execute cleanup');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getIssueChips = (issues: any) => {
    const chips = [];
    if (issues.emptyArtistName) chips.push(<Chip key="name" label="Empty Name" color="error" size="small" />);
    if (issues.noMembers) chips.push(<Chip key="members" label="No Members" color="warning" size="small" />);
    if (issues.noContent) chips.push(<Chip key="content" label="No Content" color="info" size="small" />);
    return chips;
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <CleaningServices color="primary" fontSize="large" />
            <Box>
              <Typography variant="h5" gutterBottom>
                Database Cleanup Tool
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Remove malformed and incomplete band recommendation entries
              </Typography>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {cleanupResult && (
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Cleanup completed successfully!
              </Typography>
              <Typography variant="body2">
                • Deleted {cleanupResult.statistics.deletedMalformed} malformed entries<br/>
                • Deleted {cleanupResult.statistics.deletedEmpty} empty entries<br/>
                • Fixed {cleanupResult.statistics.fixedInstrumentation} instrumentation issues<br/>
                • Total processed: {cleanupResult.statistics.totalProcessed} entries
              </Typography>
            </Alert>
          )}

          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<Visibility />}
              onClick={fetchPreview}
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} /> : 'Preview Cleanup'}
            </Button>
            
            <Button
              variant="contained"
              color="error"
              startIcon={<Delete />}
              onClick={() => setConfirmDialogOpen(true)}
              disabled={loading}
            >
              Execute Cleanup
            </Button>
          </Stack>

          <Box sx={{ mt: 3 }}>
            <Alert severity="warning">
              <Typography variant="subtitle2" gutterBottom>
                ⚠️ What this cleanup will do:
              </Typography>
              <Typography variant="body2">
                • Delete entries with empty artist names<br/>
                • Delete entries with no members and no content<br/>
                • Remove malformed instrumentation data<br/>
                • Clean up empty slot placeholders
              </Typography>
            </Alert>
          </Box>
        </CardContent>
      </Card>

      {/* Preview Dialog */}
      <Dialog 
        open={previewDialogOpen} 
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Visibility />
            Cleanup Preview
          </Box>
        </DialogTitle>
        <DialogContent>
          {preview && (
            <Box>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="subtitle2">
                  Found {preview.summary?.totalToProcess || 0} entries that will be processed:
                </Typography>
                <Typography variant="body2">
                  • {preview.summary?.totalMalformed || 0} malformed entries to delete<br/>
                  • {preview.summary?.totalEmptyInstrumentation || 0} empty instrumentation entries to delete<br/>
                  • {preview.summary?.totalMalformedInstrumentation || 0} entries with instrumentation issues to fix
                </Typography>
              </Alert>

              {preview.malformedEntries && preview.malformedEntries.length > 0 && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h6">
                      Malformed Entries ({preview.malformedEntries?.length || 0})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>Artist Name</TableCell>
                            <TableCell>Issues</TableCell>
                            <TableCell>Created</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(preview.malformedEntries || []).slice(0, 10).map((entry) => (
                            <TableRow key={entry._id}>
                              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                {entry._id.slice(-8)}
                              </TableCell>
                              <TableCell>{entry.artistName}</TableCell>
                              <TableCell>
                                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                  {getIssueChips(entry.issues)}
                                </Stack>
                              </TableCell>
                              <TableCell>{formatDate(entry.createdAt)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      {(preview.malformedEntries?.length || 0) > 10 && (
                        <Box sx={{ p: 1, textAlign: 'center', bgcolor: 'grey.50' }}>
                          <Typography variant="caption">
                            ... and {(preview.malformedEntries?.length || 0) - 10} more entries
                          </Typography>
                        </Box>
                      )}
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              )}

              {preview.malformedInstrumentationEntries && preview.malformedInstrumentationEntries.length > 0 && (
                <Accordion sx={{ mt: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h6">
                      Instrumentation Issues ({preview.malformedInstrumentationEntries?.length || 0})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Artist Name</TableCell>
                            <TableCell>Issues Count</TableCell>
                            <TableCell>Created</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(preview.malformedInstrumentationEntries || []).slice(0, 10).map((entry) => (
                            <TableRow key={entry._id}>
                              <TableCell>{entry.artistName}</TableCell>
                              <TableCell>
                                <Chip 
                                  label={`${entry.instrumentationIssues.length} issues`} 
                                  color="warning" 
                                  size="small" 
                                />
                              </TableCell>
                              <TableCell>{formatDate(entry.createdAt)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog 
        open={confirmDialogOpen} 
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning color="error" />
            Confirm Database Cleanup
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              ⚠️ This action cannot be undone!
            </Typography>
            <Typography variant="body2">
              This will permanently delete malformed database entries and fix instrumentation issues. 
              Make sure you have a database backup before proceeding.
            </Typography>
          </Alert>
          
          <Typography variant="body1">
            Are you sure you want to proceed with the database cleanup?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={executeCleanup} 
            color="error" 
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Yes, Clean Database'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DatabaseCleanup;